import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { rewardSummary, rewardHistory, rewardDebit } from '../../util/api';
import { parse } from '../../util/urlHelpers';

export const REWARD_FETCH_REQUEST = 'app/RewardPage/REWARD_FETCH_REQUEST';
export const REWARD_FETCH_SUCCESS = 'app/RewardPage/REWARD_FETCH_SUCCESS';
export const REWARD_FETCH_ERROR = 'app/RewardPage/REWARD_FETCH_ERROR';
export const REWARD_DEBIT_REQUEST = 'app/RewardPage/REWARD_DEBIT_REQUEST';
export const REWARD_DEBIT_SUCCESS = 'app/RewardPage/REWARD_DEBIT_SUCCESS';
export const REWARD_DEBIT_ERROR = 'app/RewardPage/REWARD_DEBIT_ERROR';

const initialState = {
  fetching: false,
  error: null,
  summary: {
    totalStars: 0,
    lifetimeStars: 0,
    availableCredits: 0,
    activeStreakDays: 0,
    lastLessonDate: 0,
    lastActivityDate: 0,
    badges: [],
  },
  history: [],
  pagination: { page: 1, totalPages: 1, perPage: 10 },
  debiting: false,
};

export default function rewardPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case REWARD_FETCH_REQUEST:
      return { ...state, fetching: true, error: null };
    case REWARD_FETCH_SUCCESS: {
      const { summary, history, pagination } = payload || {};
      return {
        ...state,
        fetching: false,
        error: null,
        summary: {
          totalStars: summary?.totalStars || 0,
          lifetimeStars: summary?.lifetimeStars || 0,
          availableCredits: summary?.availableCredits || 0,
          activeStreakDays: summary?.activeStreakDays || 0,
          lastLessonDate: summary?.lastLessonDate || 0,
          lastActivityDate: summary?.lastActivityDate || 0,
          badges: Array.isArray(summary?.badges) ? summary.badges : [],
        },
        history: Array.isArray(history) ? history : [],
        pagination: pagination || state.pagination,
      };
    }
    case REWARD_FETCH_ERROR:
      return { ...state, fetching: false, error: payload };
    case REWARD_DEBIT_REQUEST:
      return { ...state, debiting: true };
    case REWARD_DEBIT_SUCCESS:
      return { ...state, debiting: false };
    case REWARD_DEBIT_ERROR:
      return { ...state, debiting: false, error: payload };
    default:
      return state;
  }
}

export const rewardFetchRequest = () => ({ type: REWARD_FETCH_REQUEST });
export const rewardFetchSuccess = data => ({ type: REWARD_FETCH_SUCCESS, payload: data });
export const rewardFetchError = e => ({ type: REWARD_FETCH_ERROR, error: true, payload: e });
export const rewardDebitRequest = () => ({ type: REWARD_DEBIT_REQUEST });
export const rewardDebitSuccess = data => ({ type: REWARD_DEBIT_SUCCESS, payload: data });
export const rewardDebitError = e => ({ type: REWARD_DEBIT_ERROR, error: true, payload: e });

export const fetchRewards = ({ page = 1, limit = 10 } = {}) => async (dispatch, getState) => {
  try {
    const state = getState();
    const userId = state?.user?.currentUser?.id?.uuid;
    if (!userId) return;

    dispatch(rewardFetchRequest());

    const [sRes, hRes] = await Promise.all([
      rewardSummary({ userId }),
      rewardHistory({ userId, limit, page }),
    ]);

    const summary = (sRes && (sRes.data?.data || sRes.data)) || sRes || {};
    const historyRaw = (hRes && (hRes.data?.data || hRes.data)) || hRes || [];
    const history = Array.isArray(historyRaw)
      ? historyRaw
      : Array.isArray(historyRaw?.items)
        ? historyRaw.items
        : [];

    // Infer totalPages: if we received a full page, there might be a next page.
    // This avoids needing a separate count endpoint.
    const totalPages = history.length >= limit ? page + 1 : page;
    dispatch(rewardFetchSuccess({ summary, history, pagination: { page, totalPages, perPage: limit } }));
  } catch (e) {
    const error = storableError(e);
    log.error(error, 'reward-fetch-failed');
    dispatch(rewardFetchError(error));
  }
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const currentUser = getState()?.user?.currentUser;
  const currentUserId = currentUser?.id?.uuid;
  if (!currentUserId) {
    return Promise.resolve();
  }
  const query = parse(search);
  const { page = 1, limit = 10 } = query;

  return dispatch(fetchRewards({ page, limit })).catch(e => {
    throw e;
  });
};

export const debitStars = (amount) => async (dispatch, getState) => {
  try {
    const state = getState();
    const userId = state?.user?.currentUser?.id?.uuid;
    if (!userId) return;
    let v = parseInt(amount, 10);
    if (!v || v <= 0) {
      const total = state?.RewardPage?.summary?.totalStars || 0;
      const per = 500;
      v = Math.floor(total / per) * per;
    }
    if (!v || v <= 0) return;
    dispatch(rewardDebitRequest());
    const res = await rewardDebit({ userId, amount: v });
    dispatch(rewardDebitSuccess(res?.data || res));
    const { perPage } = state?.RewardPage?.pagination || { perPage: 10 };
    await dispatch(fetchRewards({ page: 1, limit: perPage }));
  } catch (e) {
    const error = storableError(e);
    log.error(error, 'reward-debit-failed');
    dispatch(rewardDebitError(error));
  }
};
